/**
 * S3 Metadata Cache - In-memory caching for S3 listObjects responses
 *
 * Provides efficient caching of S3 folder/file listings to reduce redundant API calls
 * and improve navigation performance in the S3 file browser.
 *
 * Features:
 * - Granular prefix-based caching with partition support
 * - Cache statistics and metrics (hit/miss rates, API call tracking)
 * - Precomputed filter metadata caching (partners, years, months)
 * - Configurable TTL and LRU eviction
 * - Cache presets for different use cases
 * - Cache warming utilities for frequently accessed data
 * - Server-side cache coordination readiness
 */
/**
 * Cache preset configurations for different use cases
 */
export var CachePreset;
(function (CachePreset) {
    /** Development: Short TTL, smaller cache, verbose logging */
    CachePreset["DEVELOPMENT"] = "development";
    /** Production: Balanced settings for general use */
    CachePreset["PRODUCTION"] = "production";
    /** High Performance: Long TTL, large cache for heavy traffic */
    CachePreset["HIGH_PERFORMANCE"] = "high-performance";
    /** Low Memory: Minimal cache for resource-constrained environments */
    CachePreset["LOW_MEMORY"] = "low-memory";
    /** Testing: Very short TTL for testing scenarios */
    CachePreset["TESTING"] = "testing";
})(CachePreset || (CachePreset = {}));
/**
 * Get preset configuration for cache options
 */
export function getCachePreset(preset) {
    switch (preset) {
        case CachePreset.DEVELOPMENT:
            return {
                ttlMs: 2 * 60 * 1000, // 2 minutes
                maxEntries: 50,
                debug: true,
                enablePartitionCaching: true,
                enableFilterMetadata: true,
                enableCacheWarming: false,
            };
        case CachePreset.PRODUCTION:
            return {
                ttlMs: 5 * 60 * 1000, // 5 minutes
                maxEntries: 100,
                debug: false,
                enablePartitionCaching: true,
                enableFilterMetadata: true,
                enableCacheWarming: false,
            };
        case CachePreset.HIGH_PERFORMANCE:
            return {
                ttlMs: 15 * 60 * 1000, // 15 minutes
                maxEntries: 500,
                debug: false,
                enablePartitionCaching: true,
                enableFilterMetadata: true,
                enableCacheWarming: true,
                warmUpPrefixes: ['reports/', 'partners/'],
            };
        case CachePreset.LOW_MEMORY:
            return {
                ttlMs: 3 * 60 * 1000, // 3 minutes
                maxEntries: 25,
                debug: false,
                enablePartitionCaching: false,
                enableFilterMetadata: false,
                enableCacheWarming: false,
            };
        case CachePreset.TESTING:
            return {
                ttlMs: 30 * 1000, // 30 seconds
                maxEntries: 10,
                debug: true,
                enablePartitionCaching: true,
                enableFilterMetadata: true,
                enableCacheWarming: false,
            };
        default:
            return {};
    }
}
/**
 * S3 Metadata Cache class for storing and retrieving S3 listObjects responses
 * with granular partitioning and filter metadata precomputation
 */
export class S3MetadataCache {
    constructor(options = {}) {
        this.cache = new Map();
        this.partitionCache = new Map(); // Partition-specific cache
        this.filterMetadata = null;
        // Statistics tracking
        this.hits = 0;
        this.misses = 0;
        this.totalApiCalls = 0;
        this.hitTimings = [];
        this.totalBytesServed = 0;
        this.evictions = 0;
        this.expirations = 0;
        this.statsResetTime = Date.now();
        this.cacheWarmedPrefixes = new Set();
        this.MAX_TIMING_SAMPLES = 100;
        this.queryLatencies = []; // Track S3 API latencies
        this.cacheHitLatencies = []; // Track cache hit latencies
        this.prefixAccessCounts = new Map(); // Track prefix popularity
        this.warmCacheHits = 0; // Track hits from warmed entries
        this.lastHealthCheck = Date.now();
        this.healthCheckInterval = 60000; // 1 minute
        // Apply preset if provided
        const presetOptions = options.preset ? getCachePreset(options.preset) : {};
        const mergedOptions = { ...presetOptions, ...options };
        this.options = {
            ttlMs: mergedOptions.ttlMs ?? 5 * 60 * 1000, // 5 minutes default
            maxEntries: mergedOptions.maxEntries ?? 100,
            debug: mergedOptions.debug ?? false,
            enablePartitionCaching: mergedOptions.enablePartitionCaching ?? true,
            enableFilterMetadata: mergedOptions.enableFilterMetadata ?? true,
            enableCacheWarming: mergedOptions.enableCacheWarming ?? false,
            warmUpPrefixes: mergedOptions.warmUpPrefixes ?? [],
        };
    }
    /**
     * Generate cache key from path and pagination parameters
     */
    generateCacheKey(prefix, delimiter = '/', maxKeys = 1000, continuationToken) {
        const baseKey = `${prefix}|${delimiter}|${maxKeys}`;
        return continuationToken ? `${baseKey}|${continuationToken}` : baseKey;
    }
    /**
     * Check if cache entry is expired
     */
    isExpired(entry) {
        return Date.now() > entry.expiresAt;
    }
    /**
     * Clean up expired entries from cache
     */
    cleanupExpiredEntries() {
        const now = Date.now();
        const keysToDelete = [];
        this.cache.forEach((entry, key) => {
            if (now > entry.expiresAt) {
                keysToDelete.push(key);
            }
        });
        keysToDelete.forEach(key => {
            this.cache.delete(key);
            this.expirations++;
            if (this.options.debug) {
                console.log(`S3Cache: Removed expired entry for key: ${key}`);
            }
        });
    }
    /**
     * Enforce cache size limits by removing oldest entries
     */
    enforceSizeLimits() {
        if (this.cache.size <= this.options.maxEntries)
            return;
        // Convert to array and sort by timestamp (oldest first)
        const entries = Array.from(this.cache.entries()).sort(([, a], [, b]) => a.timestamp - b.timestamp);
        // Remove oldest entries until we're under the limit
        const excessEntries = entries.slice(0, this.cache.size - this.options.maxEntries);
        for (const [key] of excessEntries) {
            this.cache.delete(key);
            this.evictions++;
            if (this.options.debug) {
                console.log(`S3Cache: Removed LRU entry for key: ${key}`);
            }
        }
    }
    /**
     * Store S3 list result in cache
     */
    set(data, prefix, delimiter = '/', maxKeys = 1000, continuationToken) {
        const key = this.generateCacheKey(prefix, delimiter, maxKeys, continuationToken);
        const now = Date.now();
        const entry = {
            data,
            timestamp: now,
            expiresAt: now + this.options.ttlMs,
        };
        this.cache.set(key, entry);
        if (this.options.debug) {
            console.log(`S3Cache: Cached ${data.items.length} items for prefix: "${prefix}"`);
        }
        // Cleanup to maintain cache health
        this.cleanupExpiredEntries();
        this.enforceSizeLimits();
    }
    /**
     * Retrieve cached S3 list result
     */
    get(prefix, delimiter = '/', maxKeys = 1000, continuationToken) {
        const key = this.generateCacheKey(prefix, delimiter, maxKeys, continuationToken);
        const entry = this.cache.get(key);
        const startTime = Date.now();
        if (!entry) {
            this.misses++;
            if (this.options.debug) {
                console.log(`S3Cache: Cache miss for prefix: "${prefix}"`);
            }
            return null;
        }
        if (this.isExpired(entry)) {
            this.cache.delete(key);
            this.misses++;
            this.expirations++;
            if (this.options.debug) {
                console.log(`S3Cache: Expired entry removed for prefix: "${prefix}"`);
            }
            return null;
        }
        this.hits++;
        const elapsed = Date.now() - startTime;
        this.recordHitTiming(elapsed);
        this.recordCacheHitLatency(elapsed);
        // Track bytes served from cache (approximate)
        const bytesServed = this.estimateCacheEntrySize(entry.data);
        this.totalBytesServed += bytesServed;
        // Track prefix access count
        this.trackPrefixAccess(prefix);
        // Track if this was a warmed cache hit
        if (this.cacheWarmedPrefixes.has(prefix)) {
            this.warmCacheHits++;
        }
        // Update entry access metadata for better LRU
        entry.accessCount = (entry.accessCount || 0) + 1;
        entry.lastAccessed = Date.now();
        if (this.options.debug) {
            console.log(`S3Cache: Cache hit for prefix: "${prefix}" (${entry.data.items.length} items, ${elapsed}ms, ~${bytesServed} bytes)`);
        }
        return entry.data;
    }
    /**
     * Check if data exists in cache for given parameters
     */
    has(prefix, delimiter = '/', maxKeys = 1000, continuationToken) {
        const key = this.generateCacheKey(prefix, delimiter, maxKeys, continuationToken);
        const entry = this.cache.get(key);
        return entry !== undefined && !this.isExpired(entry);
    }
    /**
     * Invalidate cache entries for a specific prefix (useful after uploads/deletes)
     */
    invalidatePrefix(prefix) {
        let removedCount = 0;
        const keysToDelete = [];
        this.cache.forEach((_, key) => {
            if (key.startsWith(prefix + '|') || key === prefix + '|/|1000') {
                keysToDelete.push(key);
            }
        });
        keysToDelete.forEach(key => {
            this.cache.delete(key);
            removedCount++;
        });
        if (this.options.debug && removedCount > 0) {
            console.log(`S3Cache: Invalidated ${removedCount} entries for prefix: "${prefix}"`);
        }
    }
    /**
     * Clear all cached entries (main, partition, and filter metadata)
     */
    clear() {
        const size = this.cache.size;
        const partitionSize = this.partitionCache.size;
        this.cache.clear();
        this.partitionCache.clear();
        this.invalidateFilterMetadata();
        if (this.options.debug) {
            console.log(`S3Cache: Cleared all entries - Main: ${size}, Partition: ${partitionSize}, Filter metadata: 1`);
        }
    }
    /**
     * Track an API call (called by S3Service after making S3 API request)
     */
    trackApiCall() {
        this.totalApiCalls++;
    }
    /**
     * Record cache hit timing for performance monitoring
     */
    recordHitTiming(timeMs) {
        this.hitTimings.push(timeMs);
        if (this.hitTimings.length > this.MAX_TIMING_SAMPLES) {
            this.hitTimings.shift();
        }
    }
    /**
     * Record cache hit latency for monitoring
     */
    recordCacheHitLatency(latencyMs) {
        this.cacheHitLatencies.push(latencyMs);
        if (this.cacheHitLatencies.length > this.MAX_TIMING_SAMPLES) {
            this.cacheHitLatencies.shift();
        }
    }
    /**
     * Record S3 API query latency
     */
    recordQueryLatency(latencyMs) {
        this.queryLatencies.push(latencyMs);
        if (this.queryLatencies.length > this.MAX_TIMING_SAMPLES) {
            this.queryLatencies.shift();
        }
    }
    /**
     * Track prefix access for popularity metrics
     */
    trackPrefixAccess(prefix) {
        const count = this.prefixAccessCounts.get(prefix) || 0;
        this.prefixAccessCounts.set(prefix, count + 1);
    }
    /**
     * Calculate average hit time in milliseconds
     */
    getAverageHitTime() {
        if (this.hitTimings.length === 0)
            return undefined;
        const sum = this.hitTimings.reduce((a, b) => a + b, 0);
        return sum / this.hitTimings.length;
    }
    /**
     * Calculate average cache hit latency
     */
    getAverageCacheHitLatency() {
        if (this.cacheHitLatencies.length === 0)
            return undefined;
        const sum = this.cacheHitLatencies.reduce((a, b) => a + b, 0);
        return sum / this.cacheHitLatencies.length;
    }
    /**
     * Calculate average S3 API query latency
     */
    getAverageQueryLatency() {
        if (this.queryLatencies.length === 0)
            return undefined;
        const sum = this.queryLatencies.reduce((a, b) => a + b, 0);
        return sum / this.queryLatencies.length;
    }
    /**
     * Get top N most accessed prefixes
     */
    getTopPrefixes(n = 10) {
        const entries = Array.from(this.prefixAccessCounts.entries());
        return entries
            .sort((a, b) => b[1] - a[1])
            .slice(0, n)
            .map(([prefix, count]) => ({ prefix, count }));
    }
    /**
     * Estimate cache entry size in bytes (rough approximation)
     */
    estimateCacheEntrySize(data) {
        // Rough estimate: JSON string length as proxy for memory size
        try {
            return JSON.stringify(data).length;
        }
        catch {
            // Fallback: estimate based on item count
            return data.items.length * 200; // ~200 bytes per item average
        }
    }
    /**
     * Get comprehensive cache statistics
     */
    getStats() {
        const total = this.hits + this.misses;
        const hitRate = total === 0 ? 0 : (this.hits / total) * 100;
        const apiCallsSaved = total > 0 ? this.hits : 0; // Each cache hit = 1 API call saved
        return {
            size: this.cache.size,
            maxSize: this.options.maxEntries,
            ttlMs: this.options.ttlMs,
            hits: this.hits,
            misses: this.misses,
            hitRate: Number(hitRate.toFixed(2)),
            totalApiCalls: this.totalApiCalls,
            partitionCacheSize: this.partitionCache.size,
            filterMetadataSize: this.filterMetadata ? 1 : 0,
            avgHitMs: this.getAverageHitTime(),
            totalBytesServed: this.totalBytesServed,
            apiCallsSaved,
            evictions: this.evictions,
            expirations: this.expirations,
            lastResetTime: this.statsResetTime,
            avgQueryLatencyMs: this.getAverageQueryLatency(),
            avgCacheHitLatencyMs: this.getAverageCacheHitLatency(),
            prefixAccessCounts: this.prefixAccessCounts,
            warmCacheHits: this.warmCacheHits,
        };
    }
    /**
     * Reset statistics (useful for benchmarking)
     */
    resetStatistics() {
        this.hits = 0;
        this.misses = 0;
        this.totalApiCalls = 0;
        this.hitTimings = [];
        this.totalBytesServed = 0;
        this.evictions = 0;
        this.expirations = 0;
        this.statsResetTime = Date.now();
        this.queryLatencies = [];
        this.cacheHitLatencies = [];
        this.prefixAccessCounts.clear();
        this.warmCacheHits = 0;
    }
    /**
     * Extract partition from S3 prefix for partition-based caching
     * Examples: "reports/2024/" -> "reports", "partners/acme/" -> "partners/acme"
     */
    getPartitionKey(prefix) {
        if (!prefix || prefix === '')
            return 'root';
        // Split by / and get the first non-empty segment
        const parts = prefix.split('/').filter(p => p);
        if (parts.length === 0)
            return 'root';
        // For deeper paths, use first 2 segments as partition
        return parts.slice(0, 2).join('/');
    }
    /**
     * Set data with partition-based caching support
     */
    setWithPartition(data, prefix, delimiter = '/', maxKeys = 1000, continuationToken) {
        // Call regular set first
        this.set(data, prefix, delimiter, maxKeys, continuationToken);
        // Also cache by partition for faster aggregation queries
        if (this.options.enablePartitionCaching && !continuationToken) {
            const partitionKey = this.getPartitionKey(prefix);
            const partitionCacheKey = `${partitionKey}|${delimiter}|${maxKeys}`;
            const now = Date.now();
            const entry = {
                data,
                timestamp: now,
                expiresAt: now + this.options.ttlMs,
            };
            this.partitionCache.set(partitionCacheKey, entry);
            if (this.options.debug) {
                console.log(`S3Cache: Cached by partition "${partitionKey}"`);
            }
        }
    }
    /**
     * Get data from partition cache (for aggregated queries across partitions)
     */
    getFromPartition(partitionPrefix, delimiter = '/', maxKeys = 1000) {
        const partitionCacheKey = `${partitionPrefix}|${delimiter}|${maxKeys}`;
        const entry = this.partitionCache.get(partitionCacheKey);
        if (!entry) {
            this.misses++;
            return null;
        }
        if (this.isExpired(entry)) {
            this.partitionCache.delete(partitionCacheKey);
            this.misses++;
            return null;
        }
        this.hits++;
        if (this.options.debug) {
            console.log(`S3Cache: Partition cache hit for "${partitionPrefix}"`);
        }
        return entry.data;
    }
    /**
     * Invalidate partition cache entries
     */
    invalidatePartition(partitionPrefix) {
        let removedCount = 0;
        const keysToDelete = [];
        this.partitionCache.forEach((_, key) => {
            if (key.startsWith(partitionPrefix + '|')) {
                keysToDelete.push(key);
            }
        });
        keysToDelete.forEach(key => {
            this.partitionCache.delete(key);
            removedCount++;
        });
        if (this.options.debug && removedCount > 0) {
            console.log(`S3Cache: Invalidated ${removedCount} partition entries for: "${partitionPrefix}"`);
        }
    }
    /**
     * Cache precomputed filter metadata (partners, years, months)
     * Used to avoid repeated extraction from full S3 listings
     */
    cacheFilterMetadata(partners, years, months) {
        if (!this.options.enableFilterMetadata)
            return;
        const now = Date.now();
        this.filterMetadata = {
            partners,
            years,
            months,
            lastUpdated: now,
            expiresAt: now + this.options.ttlMs,
        };
        if (this.options.debug) {
            console.log(`S3Cache: Cached filter metadata - Partners: ${partners.size}, Years: ${years.size}, Months: ${months.size}`);
        }
    }
    /**
     * Get precomputed filter metadata if available and not expired
     */
    getFilterMetadata() {
        if (!this.filterMetadata || !this.options.enableFilterMetadata)
            return null;
        if (Date.now() > this.filterMetadata.expiresAt) {
            this.filterMetadata = null;
            return null;
        }
        this.hits++;
        if (this.options.debug) {
            console.log(`S3Cache: Filter metadata cache hit`);
        }
        return this.filterMetadata;
    }
    /**
     * Invalidate filter metadata cache
     */
    invalidateFilterMetadata() {
        if (this.filterMetadata) {
            this.filterMetadata = null;
            if (this.options.debug) {
                console.log(`S3Cache: Filter metadata cache invalidated`);
            }
        }
    }
    /**
     * Manually trigger cleanup of expired entries
     */
    cleanup() {
        const sizeBefore = this.cache.size;
        const partitionSizeBefore = this.partitionCache.size;
        this.cleanupExpiredEntries();
        // Cleanup partition cache
        const now = Date.now();
        const keysToDelete = [];
        this.partitionCache.forEach((entry, key) => {
            if (now > entry.expiresAt) {
                keysToDelete.push(key);
            }
        });
        keysToDelete.forEach(key => {
            this.partitionCache.delete(key);
            this.expirations++;
        });
        const sizeAfter = this.cache.size;
        const partitionSizeAfter = this.partitionCache.size;
        if (this.options.debug && (sizeBefore !== sizeAfter || partitionSizeBefore !== partitionSizeAfter)) {
            console.log(`S3Cache: Cleanup - Main: ${sizeBefore - sizeAfter} removed, Partition: ${partitionSizeBefore - partitionSizeAfter} removed`);
        }
    }
    /**
     * Generate a detailed performance report for logging/monitoring
     */
    getPerformanceReport() {
        const stats = this.getStats();
        const uptime = Date.now() - (stats.lastResetTime || Date.now());
        const uptimeMinutes = (uptime / 60000).toFixed(2);
        const topPrefixes = this.getTopPrefixes(5);
        const report = [
            '=== S3 Cache Performance Report ===',
            `Uptime: ${uptimeMinutes} minutes`,
            `Cache Size: ${stats.size}/${stats.maxSize} entries`,
            `Partition Cache: ${stats.partitionCacheSize} entries`,
            `Filter Metadata: ${stats.filterMetadataSize ? 'Cached' : 'Not cached'}`,
            '',
            '--- Hit/Miss Statistics ---',
            `Total Requests: ${stats.hits + stats.misses}`,
            `Cache Hits: ${stats.hits}`,
            `Cache Misses: ${stats.misses}`,
            `Hit Rate: ${stats.hitRate}%`,
            `Warm Cache Hits: ${stats.warmCacheHits}`,
            `Average Hit Time: ${stats.avgHitMs?.toFixed(2) || 'N/A'} ms`,
            '',
            '--- Latency Metrics ---',
            `Avg Cache Hit Latency: ${stats.avgCacheHitLatencyMs?.toFixed(2) || 'N/A'} ms`,
            `Avg S3 API Latency: ${stats.avgQueryLatencyMs?.toFixed(2) || 'N/A'} ms`,
            stats.avgCacheHitLatencyMs && stats.avgQueryLatencyMs
                ? `Speedup Factor: ${(stats.avgQueryLatencyMs / stats.avgCacheHitLatencyMs).toFixed(2)}x`
                : '',
            '',
            '--- API Call Optimization ---',
            `Total S3 API Calls: ${stats.totalApiCalls}`,
            `API Calls Saved: ${stats.apiCallsSaved}`,
            `Reduction: ${stats.totalApiCalls > 0 ? ((stats.apiCallsSaved / (stats.totalApiCalls + stats.apiCallsSaved)) * 100).toFixed(2) : 0}%`,
            '',
            '--- Cache Management ---',
            `Evictions (LRU): ${stats.evictions}`,
            `Expirations (TTL): ${stats.expirations}`,
            `Bytes Served: ${(stats.totalBytesServed / 1024).toFixed(2)} KB`,
            '',
            '--- Top Accessed Prefixes ---',
            ...topPrefixes.map((p, i) => `${i + 1}. "${p.prefix}" (${p.count} accesses)`),
            '================================='
        ].filter(line => line !== ''); // Remove empty strings from conditional content
        return report.join('\n');
    }
    /**
     * Log performance report to console
     */
    logPerformanceReport() {
        console.log(this.getPerformanceReport());
    }
    /**
     * Warm cache with pre-specified prefixes (should be called by S3Service)
     * Marks prefixes as warmed for tracking purposes
     */
    markPrefixAsWarmed(prefix) {
        this.cacheWarmedPrefixes.add(prefix);
        if (this.options.debug) {
            console.log(`S3Cache: Marked prefix "${prefix}" as warmed`);
        }
    }
    /**
     * Check if a prefix is in the warmed set
     */
    isPrefixWarmed(prefix) {
        return this.cacheWarmedPrefixes.has(prefix);
    }
    /**
     * Get list of warmed prefixes
     */
    getWarmedPrefixes() {
        return Array.from(this.cacheWarmedPrefixes);
    }
    /**
     * Perform cache health check (automatically runs periodically)
     */
    performHealthCheck() {
        const issues = [];
        const recommendations = [];
        const stats = this.getStats();
        // Check hit rate
        if (stats.hitRate < 30 && (stats.hits + stats.misses) > 10) {
            issues.push(`Low cache hit rate: ${stats.hitRate}%`);
            recommendations.push('Consider increasing TTL or enabling cache warming');
        }
        // Check cache utilization
        const utilizationPercent = (stats.size / stats.maxSize) * 100;
        if (utilizationPercent > 90) {
            issues.push(`High cache utilization: ${utilizationPercent.toFixed(1)}%`);
            recommendations.push('Consider increasing maxEntries');
        }
        // Check eviction rate
        const requestCount = stats.hits + stats.misses;
        if (requestCount > 0 && stats.evictions > requestCount * 0.2) {
            issues.push(`High eviction rate: ${stats.evictions} evictions vs ${requestCount} requests`);
            recommendations.push('Cache size may be too small for workload');
        }
        // Check if cache warming is beneficial
        if (!this.options.enableCacheWarming && stats.misses > stats.hits && requestCount > 20) {
            recommendations.push('Consider enabling cache warming for frequently accessed paths');
        }
        this.lastHealthCheck = Date.now();
        return {
            healthy: issues.length === 0,
            issues,
            recommendations,
        };
    }
    /**
     * Get health check status
     */
    getHealthStatus() {
        const health = this.performHealthCheck();
        if (health.healthy) {
            return '✅ Cache is healthy';
        }
        const lines = ['⚠️ Cache Health Issues:'];
        health.issues.forEach(issue => lines.push(`  - ${issue}`));
        if (health.recommendations.length > 0) {
            lines.push('Recommendations:');
            health.recommendations.forEach(rec => lines.push(`  - ${rec}`));
        }
        return lines.join('\n');
    }
}
// Export singleton instance for global use
let defaultCache = null;
/**
 * Get the default S3 metadata cache instance
 */
export function getS3Cache(options) {
    if (!defaultCache) {
        defaultCache = new S3MetadataCache(options);
    }
    return defaultCache;
}
/**
 * Reset the default cache instance (useful for testing)
 */
export function resetS3Cache() {
    defaultCache = null;
}
export default S3MetadataCache;
